import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  operations as siteOperations,
  selectors as siteSelectors,
  actions as siteActions,
} from 'store/siteManager'
import { Formik } from 'formik'
import {
  additionalGoalFields,
  validationSchema,
  optionalFieldNames,
  requiredFieldNames,
  fieldNames,
} from './data'
import GoalsModalComponent from './component'
import {
  classCreationGoalValueName,
  lessonCompletionGoalValueName,
} from './AddGoals/data'

export const GoalsModalContainer = ({
  clearError,
  flowLabel,
  handleCancel,
  hasApiError,
  handleClose,
  modals,
  preferences,
  siteId,
  updateSitePreferences,
}) => {
  // just using one of the required goals to know if the fields have been completed once
  const areRequiredGoalsComplete = !!preferences?.classCreationGoalValueIsCompleted

  const goal3HasValues =
    (!areRequiredGoalsComplete &&
      (!!preferences?.goal3Text || !!preferences?.goal3Date)) ||
    (areRequiredGoalsComplete && preferences.goal3TextIsCompleted)
  const goal4HasValues =
    (!areRequiredGoalsComplete &&
      (!!preferences?.goal4Text || !!preferences?.goal4Date)) ||
    (areRequiredGoalsComplete && preferences.goal4TextIsCompleted)
  const goal5HasValues =
    (!areRequiredGoalsComplete &&
      (!!preferences?.goal5Text || !!preferences?.goal5Date)) ||
    (areRequiredGoalsComplete && preferences.goal5TextIsCompleted)

  const showGoal3 = goal3HasValues || goal4HasValues || goal5HasValues || false
  const showGoal4 = goal4HasValues || goal5HasValues || false
  const showGoal5 = goal5HasValues || false

  const [currentStep, setCurrentStep] = useState(
    areRequiredGoalsComplete ? 1 : 0,
  )
  const [additionalGoalCount, setAdditionalGoalCount] = useState(
    showGoal3 + showGoal4 + showGoal5, // boolean javascript magic
  )

  const handleStepChange = toStep => setCurrentStep(toStep)

  const goBack = () => {
    handleStepChange(currentStep - 1)
  }

  const closeModal = () => {
    clearError()
    handleClose()
  }

  const handleSave = async (values, isComplete) => {
    const newSitePreferences = {
      k5Digital: {},
      mspDigital: {},
    }
    requiredFieldNames.forEach(field => {
      const isCompletedField = `${field}IsCompleted`
      newSitePreferences.k5Digital[field] = values[field]
      newSitePreferences.mspDigital[field] = values[field]
      newSitePreferences.k5Digital[isCompletedField] = isComplete
      newSitePreferences.mspDigital[isCompletedField] = isComplete
    })
    optionalFieldNames.forEach((field, index) => {
      const fieldIndex = Math.floor(index / 2)
      const isGoalEnabled = additionalGoalCount > fieldIndex
      const isCompletedField = `${field}IsCompleted`
      newSitePreferences.k5Digital[field] = values[field]
      newSitePreferences.mspDigital[field] = values[field]
      const completedValue = isComplete && isGoalEnabled
      newSitePreferences.k5Digital[isCompletedField] = completedValue
      newSitePreferences.mspDigital[isCompletedField] = completedValue
    })

    await updateSitePreferences(siteId, newSitePreferences)
  }

  const handleChangeGoalCount = (delta, index, setFieldValue, values) => {
    const newGoalCount = additionalGoalCount + delta
    const isDecrement = delta === -1

    if (isDecrement && newGoalCount >= 0) {
      if (index !== undefined && index < additionalGoalCount - 1) {
        for (let i = index; i < additionalGoalCount - 1; i++) {
          const currentGoal = additionalGoalFields[i]
          const nextGoal = additionalGoalFields[i + 1]
          setFieldValue(currentGoal.text, values[nextGoal.text])
          setFieldValue(currentGoal.date, values[nextGoal.date])
        }
        const lastGoal = additionalGoalFields[additionalGoalCount - 1]
        setFieldValue(lastGoal.text, null)
        setFieldValue(lastGoal.date, null)
        setFieldValue(lastGoal.show, false)
      } else {
        const adjustedGoalFields = additionalGoalFields[newGoalCount]
        setFieldValue(adjustedGoalFields.date, null)
        setFieldValue(adjustedGoalFields.text, null)
        setFieldValue(adjustedGoalFields.show, false)
      }
    } else if (newGoalCount - 1 >= 0) {
      setFieldValue(additionalGoalFields[newGoalCount - 1].show, true)
      setFieldValue(additionalGoalFields[newGoalCount - 1].text, null)
      setFieldValue(additionalGoalFields[newGoalCount - 1].date, null)
    }

    setAdditionalGoalCount(newGoalCount)
  }

  const handleNextButtonClick = async (
    shouldSave,
    isComplete,
    setSubmitting,
    setTouched,
    values,
  ) => {
    const isValidSync = validationSchema.isValidSync(values)
    if (shouldSave && !isValidSync) {
      const touchedFields = fieldNames.reduce((acc, name) => {
        acc[name] = true
        return acc
      }, {})
      await setTouched(touchedFields)
      return
    }
    if (shouldSave) {
      setSubmitting(true)
      await handleSave(values, isComplete)
      setSubmitting(false)
    }

    if (areRequiredGoalsComplete) {
      closeModal()
    } else {
      handleStepChange(currentStep + 1)
    }
  }

  return (
    <Formik
      enableReinitialize
      initialTouched={{
        classCreationGoalDate: !!preferences?.classCreationGoalDate,
        classCreationGoalValue: !!preferences?.classCreationGoalValue,
        lessonCompletionGoalDate: !!preferences?.lessonCompletionGoalDate,
        lessonCompletionGoalValue: !!preferences?.lessonCompletionGoalValue,
      }}
      initialValues={{
        ...preferences,
        classCreationGoalDate: preferences?.classCreationGoalDate
          ? new Date(preferences.classCreationGoalDate)
          : null,
        lessonCompletionGoalDate: preferences?.lessonCompletionGoalDate
          ? new Date(preferences.lessonCompletionGoalDate)
          : null,
        showGoal3,
        showGoal4,
        showGoal5,
        goal3Date: preferences?.goal3Date
          ? new Date(preferences.goal3Date)
          : null,
        goal4Date: preferences?.goal4Date
          ? new Date(preferences.goal4Date)
          : null,
        goal5Date: preferences?.goal5Date
          ? new Date(preferences.goal5Date)
          : null,
      }}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        setTouched,
        isSubmitting,
        isValid,
      }) => (
        <GoalsModalComponent
          additionalGoalCount={additionalGoalCount}
          currentStep={currentStep}
          errors={errors}
          flowLabel={flowLabel}
          goBack={() => goBack(setFieldValue)}
          handleCancel={handleCancel}
          handleCancelButtonClick={async (shouldSave, isComplete) => {
            if (shouldSave) {
              setSubmitting(true)
              await handleSave(values, isComplete)
              setSubmitting(false)
            }
            closeModal()
          }}
          handleChange={(name, value) => {
            if (
              name === classCreationGoalValueName ||
              name === lessonCompletionGoalValueName
            ) {
              value = Math.floor(Number(value))
            }
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleChangeGoalCount={(delta, index) =>
            handleChangeGoalCount(delta, index, setFieldValue, values)
          }
          handleClose={() => {
            clearError('sitePreferences')
            handleClose()
          }}
          handleNextButtonClick={async (shouldSave, isComplete) =>
            handleNextButtonClick(
              shouldSave,
              isComplete,
              setSubmitting,
              setTouched,
              values,
            )
          }
          handleStepChange={handleStepChange}
          hasApiError={hasApiError}
          isComplete={areRequiredGoalsComplete}
          isSubmitting={isSubmitting}
          isValid={isValid}
          modals={modals}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

const mapStateToProps = (state, ownProps) => {
  const site = siteSelectors?.selectSiteById(state, ownProps.siteId)
  const { sitePreferences = {} } = site || {}
  const { k5Digital = {} } = sitePreferences
  const { ...preferences } = k5Digital

  return {
    hasApiError: !!sitePreferences?.error,
    preferences,
  }
}

const mapDispatchToProps = {
  updateSitePreferences: siteOperations.updateSitePreferences,
  clearError: siteActions.clearError,
}

GoalsModalContainer.propTypes = {
  clearError: PropTypes.func,
  currentStep: PropTypes.number,
  flowLabel: PropTypes.string,
  handleCancel: PropTypes.func,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  modals: PropTypes.array,
  preferences: PropTypes.shape({
    classCreationGoalValue: PropTypes.number,
    classCreationGoalValueIsCompleted: PropTypes.bool,
    classCreationGoalDate: PropTypes.string,
    lessonCompletionGoalValue: PropTypes.number,
    lessonCompletionGoalDate: PropTypes.string,
    goal3Date: PropTypes.string,
    goal3Text: PropTypes.string,
    goal3TextIsCompleted: PropTypes.bool,
    goal4Date: PropTypes.string,
    goal4Text: PropTypes.string,
    goal4TextIsCompleted: PropTypes.bool,
    goal5Date: PropTypes.string,
    goal5Text: PropTypes.string,
    goal5TextIsCompleted: PropTypes.bool,
  }),
  siteId: PropTypes.number,
  updateSitePreferences: PropTypes.func,
}

const mapper = entry => {
  const { flowLabel, modals } = entry || {}
  return {
    flowLabel,
    modals,
  }
}

const options = {
  entryId: '5hOmSQFwOUHZlaHbC6Uia2',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    GoalsModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
